<script>
    import { onMount, onDestroy, createEventDispatcher } from 'svelte';
    import { portal } from 'svelte-portal';
    
    const dispatch = createEventDispatcher();
    
    // Props
    export let show = true;
    export let title = '';
    export let initialPosition = { x: 100, y: 100 };
    export let initialWidth = 400;
    export let initialHeight = 500;
    export let minWidth = 300;
    export let minHeight = 200;
    export let maxWidth = 800;
    export let maxHeight = 800;
    export let storageKey = ''; // For persisting position/size to localStorage
    export let zIndex = null; // Legacy numeric z-index support
    export let isModal = false; // Set to true for modal-like behavior with higher z-index
    export let resizable = true;
    export let draggable = true;
    export let showCloseButton = true;
    export let panelClass = '';
    export let headerClass = '';
    export let contentClass = '';
    export let theme = ''; // 'comments', 'diff', 'search', 'navigation'
    export let resizeHandleSize = 8;
    export let showHeader = true;
    export let preventBodyScroll = false;
    
    // State
    let panelElement;
    let position = { ...initialPosition };
    let width = initialWidth;
    let height = initialHeight;
    let isDragging = false;
    let isResizing = false;
    let resizeDirection = '';
    let dragOffset = { x: 0, y: 0 };
    let resizeStart = { x: 0, y: 0, width: 0, height: 0 };
    
    // Edge/corner detection for resize
    $: RESIZE_HANDLE_SIZE = resizeHandleSize;
    
    // Initialize panel (no localStorage - positions reset on page refresh)
    onMount(() => {
        // Positions persist during session but reset to initialPosition/initialWidth/initialHeight on refresh

        // Ensure panel is within viewport
        constrainToViewport();
        
        // Add global event listeners
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        window.addEventListener('resize', constrainToViewport);
        
        // Prevent body scroll if requested
        if (preventBodyScroll && show) {
            document.body.style.overflow = 'hidden';
        }
    });
    
    onDestroy(() => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('resize', constrainToViewport);
        // Clean up any lingering classes
        document.body.classList.remove('dragging', 'resizing');
        
        // Restore body scroll
        if (preventBodyScroll) {
            document.body.style.overflow = '';
        }
    });
    
    // Save state (session-only - no localStorage)
    function saveState() {
        // Panel position/size persists during session via component state
        // No localStorage means positions reset to defaults on page refresh
    }
    
    // Constrain panel to viewport
    function constrainToViewport() {
        const maxX = window.innerWidth - width;
        const maxY = window.innerHeight - height;
        
        position.x = Math.max(0, Math.min(position.x, maxX));
        position.y = Math.max(0, Math.min(position.y, maxY));
        
        // Trigger reactive update
        position = position;
    }
    
    // Get resize direction based on mouse position
    function getResizeDirection(e) {
        if (!resizable || !panelElement) return '';
        
        const rect = panelElement.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const onLeftEdge = x < RESIZE_HANDLE_SIZE;
        const onRightEdge = x > rect.width - RESIZE_HANDLE_SIZE;
        const onTopEdge = y < RESIZE_HANDLE_SIZE;
        const onBottomEdge = y > rect.height - RESIZE_HANDLE_SIZE;
        
        if (onTopEdge && onLeftEdge) return 'nw';
        if (onTopEdge && onRightEdge) return 'ne';
        if (onBottomEdge && onLeftEdge) return 'sw';
        if (onBottomEdge && onRightEdge) return 'se';
        if (onTopEdge) return 'n';
        if (onBottomEdge) return 's';
        if (onLeftEdge) return 'w';
        if (onRightEdge) return 'e';
        
        return '';
    }
    
    // Update cursor based on resize direction
    function updateCursor(e) {
        if (!resizable || isDragging) return;
        
        const direction = getResizeDirection(e);
        const cursors = {
            'n': 'ns-resize',
            's': 'ns-resize',
            'e': 'ew-resize',
            'w': 'ew-resize',
            'ne': 'nesw-resize',
            'nw': 'nwse-resize',
            'se': 'nwse-resize',
            'sw': 'nesw-resize',
            '': 'default'
        };
        
        panelElement.style.cursor = cursors[direction];
    }
    
    // Handle mouse down for both drag and resize
    function handleMouseDown(e) {
        // Check if clicking on header for drag
        const isHeader = e.target.closest('.draggable-panel-header');
        const direction = getResizeDirection(e);
        
        if (isHeader && draggable && !direction) {
            // Start dragging
            e.preventDefault();
            isDragging = true;
            document.body.classList.add('dragging');
            const rect = panelElement.getBoundingClientRect();
            dragOffset = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            };
        } else if (direction && resizable) {
            // Start resizing
            e.preventDefault();
            isResizing = true;
            resizeDirection = direction;
            document.body.classList.add('resizing');
            resizeStart = {
                x: e.clientX,
                y: e.clientY,
                width: width,
                height: height,
                left: position.x,
                top: position.y
            };
        }
    }
    
    // Handle mouse move for drag and resize
    function handleMouseMove(e) {
        if (isDragging) {
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            
            // Constrain to viewport
            const maxX = window.innerWidth - width;
            const maxY = window.innerHeight - height;
            
            position = {
                x: Math.max(0, Math.min(newX, maxX)),
                y: Math.max(0, Math.min(newY, maxY))
            };
        } else if (isResizing) {
            const deltaX = e.clientX - resizeStart.x;
            const deltaY = e.clientY - resizeStart.y;
            
            let newWidth = width;
            let newHeight = height;
            let newX = position.x;
            let newY = position.y;
            
            // Handle width changes
            if (resizeDirection.includes('e')) {
                newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStart.width + deltaX));
            } else if (resizeDirection.includes('w')) {
                newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStart.width - deltaX));
                if (newWidth !== width) {
                    newX = resizeStart.left + (resizeStart.width - newWidth);
                }
            }
            
            // Handle height changes
            if (resizeDirection.includes('s')) {
                newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStart.height + deltaY));
            } else if (resizeDirection.includes('n')) {
                newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStart.height - deltaY));
                if (newHeight !== height) {
                    newY = resizeStart.top + (resizeStart.height - newHeight);
                }
            }
            
            // Apply changes
            width = newWidth;
            height = newHeight;
            position = { x: newX, y: newY };
            
            // Constrain to viewport
            constrainToViewport();
        }
    }
    
    // Handle mouse up
    function handleMouseUp() {
        if (isDragging || isResizing) {
            isDragging = false;
            isResizing = false;
            resizeDirection = '';
            document.body.classList.remove('dragging', 'resizing');
            saveState();
            
            // Dispatch resize event if size changed
            if (isResizing) {
                dispatch('resize', { width, height });
            }
        }
    }
    
    // Handle close
    function handleClose() {
        dispatch('close');
    }
    
    // Handle keyboard shortcuts
    function handleKeyDown(e) {
        if (e.key === 'Escape') {
            handleClose();
        } else if (draggable && e.target.closest('.draggable-panel-header')) {
            // Arrow keys for moving panel
            const step = e.shiftKey ? 10 : 1;
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    position.x = Math.max(0, position.x - step);
                    saveState();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    position.x = Math.min(window.innerWidth - width, position.x + step);
                    saveState();
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    position.y = Math.max(0, position.y - step);
                    saveState();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    position.y = Math.min(window.innerHeight - height, position.y + step);
                    saveState();
                    break;
            }
        }
    }
    
    // Reactive statement to handle theme changes and z-index
    $: themeClass = theme ? `draggable-panel--${theme}` : '';
    $: effectiveZIndex = isModal ? 1100 : (zIndex !== null ? zIndex : 10); // Use very low z-index for floating panels
    
</script>

{#if show}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
        bind:this={panelElement}
        class="draggable-panel {themeClass} {panelClass}"
        class:has-header={showHeader}
        style="
            top: {position.y}px;
            left: {position.x}px;
            z-index: {effectiveZIndex};
            width: {width}px;
            height: {height}px;
        "
        on:mousedown={handleMouseDown}
        on:mousemove={updateCursor}
        on:keydown={handleKeyDown}
        role="dialog"
        aria-label={title || 'Floating panel'}
        use:portal
    >
        {#if showHeader}
            <div 
                class="draggable-panel-header {headerClass}" 
                role="button"
                tabindex="0"
                aria-label="Drag to move panel"
            >
                <div class="panel-header-content">
                    {#if title}
                        <h3 class="panel-title">{title}</h3>
                    {/if}
                    <slot name="header" />
                </div>
                <div class="panel-header-actions">
                    <slot name="header-actions" />
                    {#if showCloseButton}
                        <button
                            class="panel-close-btn"
                            on:click={handleClose}
                            aria-label="Close panel"
                        >
                            <i class="fas fa-times"></i>
                        </button>
                    {/if}
                </div>
            </div>
        {/if}
        
        <div class="draggable-panel-content {contentClass}" class:no-header={!showHeader}>
            <slot />
        </div>
        
        <!-- Additional slot for custom content like resize handles -->
        <slot name="custom-elements" />
        
        <!-- Resize handles (visual indicators) -->
        {#if resizable}
            <div class="resize-handle resize-handle-n" />
            <div class="resize-handle resize-handle-s" />
            <div class="resize-handle resize-handle-e" />
            <div class="resize-handle resize-handle-w" />
            <div class="resize-handle resize-handle-ne" />
            <div class="resize-handle resize-handle-nw" />
            <div class="resize-handle resize-handle-se" />
            <div class="resize-handle resize-handle-sw" />
        {/if}
    </div>
{/if}

<style>
    .draggable-panel {
        position: fixed;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: var(--border-radius-3xl);
        box-shadow: var(--shadow-xl);
    }
    
    .draggable-panel-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 48px;
        padding: var(--spacing-3) var(--spacing-4);
        background: var(--color-gray-50);
        border-bottom: 1px solid var(--color-border);
        cursor: move;
        user-select: none;
    }
    
    .panel-header-content {
        display: flex;
        flex: 1;
        align-items: center;
        min-width: 0;
        gap: var(--spacing-3);
    }
    
    .panel-header-actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-2);
        flex-shrink: 0;
    }
    
    .panel-title {
        margin: 0;
        font-size: var(--font-size-base);
        font-weight: var(--font-weight-medium);
        color: var(--color-text-primary);
    }
    
    .panel-close-btn {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 28px;
        height: 28px;
        padding: 0;
        color: var(--color-text-secondary);
        background: none;
        border: none;
        border-radius: var(--radius-md);
        transition: all 0.2s ease;
        cursor: pointer;
    }
    
    .panel-close-btn:hover {
        color: var(--color-text-primary);
        background: var(--color-gray-200);
    }
    
    .draggable-panel-content {
        flex: 1;
        padding: var(--spacing-4);
        overflow: auto;
    }
    
    .draggable-panel-content.no-header {
        padding-top: var(--spacing-6);
    }
    
    /* Theme Variants */
    .draggable-panel--comments,
    .draggable-panel--diff {
        --panel-accent: var(--color-collab);
        --panel-accent-light: var(--color-collab-light);

        background: var(--color-surface-panel-collab-frosted);
        border-color: var(--color-collab-border);
        -webkit-backdrop-filter: blur(8px);
        backdrop-filter: blur(8px);
    }
    
    .draggable-panel--comments .draggable-panel-header,
    .draggable-panel--diff .draggable-panel-header {
        background: var(--color-surface-header-collab-frosted);
        border-bottom-color: var(--color-collab-border);
    }
    
    .draggable-panel--search,
    .draggable-panel--navigation {
        --panel-accent: var(--color-spec);
        --panel-accent-light: var(--color-spec-light);

        background: var(--color-surface-panel-spec-frosted);
        border-color: var(--color-spec-border);
        -webkit-backdrop-filter: blur(10px);
        backdrop-filter: blur(10px);
    }
    
    .draggable-panel--search .draggable-panel-header,
    .draggable-panel--navigation .draggable-panel-header {
        background: var(--color-surface-header-spec-frosted);
        border-bottom-color: var(--color-spec-border);
    }
    
    /* Claims theme - solid neutral styling */
    .draggable-panel--claims {
        background: var(--color-surface-content-claims) !important;
        border-color: var(--color-border) !important;
        -webkit-backdrop-filter: none !important;
        backdrop-filter: none !important;
    }
    
    .draggable-panel--claims .draggable-panel-header {
        background: var(--color-surface-content-claims) !important;
        border-bottom-color: var(--color-border) !important;
        -webkit-backdrop-filter: none !important;
        backdrop-filter: none !important;
    }
    
    /* Ensure panel is not below content */
    .draggable-panel {
        isolation: isolate;
    }
    
    /* Dark mode base styles */
    :global([data-theme="dark"]) .draggable-panel {
        background: var(--color-surface-dark);
        border-color: var(--color-border-dark);
    }
    
    :global([data-theme="dark"]) .draggable-panel-header {
        background: var(--color-gray-800);
        border-bottom-color: var(--color-border-dark);
    }
    
    :global([data-theme="dark"]) .panel-title {
        color: var(--color-text-primary-dark);
    }
    
    :global([data-theme="dark"]) .panel-close-btn {
        color: var(--color-text-secondary-dark);
    }
    
    :global([data-theme="dark"]) .panel-close-btn:hover {
        color: var(--color-text-primary-dark);
        background: var(--color-gray-700);
    }
    
    /* Dark mode theme variants */
    :global([data-theme="dark"]) .draggable-panel--comments,
    :global([data-theme="dark"]) .draggable-panel--diff {
        background: var(--color-surface-panel-collab-frosted);
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .draggable-panel--comments .draggable-panel-header,
    :global([data-theme="dark"]) .draggable-panel--diff .draggable-panel-header {
        background: var(--color-surface-header-collab-frosted);
        border-bottom-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .draggable-panel--search,
    :global([data-theme="dark"]) .draggable-panel--navigation {
        background: var(--color-surface-panel-spec-frosted);
        border-color: var(--color-spec-border);
    }
    
    :global([data-theme="dark"]) .draggable-panel--search .draggable-panel-header,
    :global([data-theme="dark"]) .draggable-panel--navigation .draggable-panel-header {
        background: var(--color-surface-header-spec-frosted);
        border-bottom-color: var(--color-spec-border);
    }
    
    /* Claims theme dark mode - solid neutral styling */
    :global([data-theme="dark"]) .draggable-panel--claims {
        background: var(--color-surface-content-claims) !important;
        border-color: var(--color-border-dark) !important;
        -webkit-backdrop-filter: none !important;
        backdrop-filter: none !important;
    }
    
    :global([data-theme="dark"]) .draggable-panel--claims .draggable-panel-header {
        background: var(--color-surface-content-claims) !important;
        border-bottom-color: var(--color-border-dark) !important;
        -webkit-backdrop-filter: none !important;
        backdrop-filter: none !important;
    }
    
    /* Resize handles */
    .resize-handle {
        position: absolute;
        z-index: 10;
        background: transparent;
    }
    
    .resize-handle-n,
    .resize-handle-s {
        right: 8px;
        left: 8px;
        height: 8px;
        cursor: ns-resize;
    }
    
    .resize-handle-n {
        top: 0;
    }
    
    .resize-handle-s {
        bottom: 0;
    }
    
    .resize-handle-e,
    .resize-handle-w {
        top: 8px;
        bottom: 8px;
        width: 8px;
        cursor: ew-resize;
    }
    
    .resize-handle-e {
        right: 0;
    }
    
    .resize-handle-w {
        left: 0;
    }
    
    .resize-handle-ne,
    .resize-handle-nw,
    .resize-handle-se,
    .resize-handle-sw {
        width: 8px;
        height: 8px;
    }
    
    .resize-handle-ne {
        top: 0;
        right: 0;
        cursor: nesw-resize;
    }
    
    .resize-handle-nw {
        top: 0;
        left: 0;
        cursor: nwse-resize;
    }
    
    .resize-handle-se {
        right: 0;
        bottom: 0;
        cursor: nwse-resize;
    }
    
    .resize-handle-sw {
        bottom: 0;
        left: 0;
        cursor: nesw-resize;
    }
    
    /* Prevent text selection while dragging/resizing */
    :global(body.dragging *),
    :global(body.resizing *) {
        user-select: none !important;
    }
</style>